import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Phone, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Logo } from "./Logo";
import { LegalAccordion } from "./LegalAccordion";
import { CONTACT } from "../data/content";

export const Footer = () => {
  const { t } = useTranslation();
  const [openPolicy, setOpenPolicy] = useState("");
  const { pathname } = useLocation();

  // Collapse any open legal panel when navigating to another page
  useEffect(() => {
    setOpenPolicy("");
  }, [pathname]);

  const openAndScroll = (id) => {
    setOpenPolicy(id);
    requestAnimationFrame(() => {
      document
        .getElementById("legal")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-brand-navy text-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Logo light className="h-9" testId="footer-logo" />
            <p className="mt-5 text-white/70 leading-relaxed max-w-lg">
              {t("footer.tagline1")}{" "}
              <br className="hidden lg:block" />
              {t("footer.tagline2")}
            </p>
          </div>

          <div className="md:col-span-5 md:col-start-8">
            <h4 className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-teal-light">
              {t("footer.talkToUs")}
            </h4>
            <ul className="mt-4 space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="h-5 w-5 shrink-0 text-brand-teal-light mt-0.5" />
                <a
                  href={CONTACT.phoneHref}
                  data-testid="footer-phone"
                  className="text-white/85 hover:text-white transition-colors"
                >
                  {CONTACT.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="h-5 w-5 shrink-0 text-brand-teal-light mt-0.5" />
                <a
                  href={`mailto:${CONTACT.email}`}
                  data-testid="footer-email"
                  className="text-white/85 hover:text-white transition-colors"
                >
                  {CONTACT.email}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <LegalAccordion value={openPolicy} onValueChange={setOpenPolicy} />

      <div className="bg-white text-slate-500 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-6 text-sm">
            <button
              data-testid="footer-privacy"
              onClick={() => openAndScroll("privacy")}
              className="hover:text-brand-navy transition-colors"
            >
              {t("footer.privacy")}
            </button>
            <button
              data-testid="footer-terms"
              onClick={() => openAndScroll("terms")}
              className="hover:text-brand-navy transition-colors"
            >
              {t("footer.terms")}
            </button>
          </div>
          <p className="text-sm text-slate-400">
            © {new Date().getFullYear()} HepCURE. {t("footer.copyright")}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
